<script lang="ts">
  import { onMount } from 'svelte'
  import { api } from '$lib/api/client'
  import type { paths } from '$lib/api/generated'
  import Table from '../../components/Table.svelte'
  import SearchInput from '../../components/SearchInput.svelte'
  import Modal from '../../components/Modal.svelte'
  import FormField from '../../components/FormField.svelte'
  import { showApiError } from '$lib/util/errors'
  import { debounce } from '$lib/util/debounce'
  import { currentRole } from '$lib/auth/session'
  import { INCIDENT_LABELS } from '$lib/incident-labels'

  type AnimalWithBreed =
    paths['/animals']['get']['responses'][200]['content']['application/json']['items'][number]
  type Breed =
    paths['/breeds']['get']['responses'][200]['content']['application/json']['items'][number]
  type IncidentItem =
    paths['/animals/{animalId}/incidents']['get']['responses'][200]['content']['application/json']['items'][number]
  type IncidentCategory = IncidentItem['category']

  const CATEGORY_OPTIONS = Object.keys(INCIDENT_LABELS) as IncidentCategory[]

  let items = $state<AnimalWithBreed[]>([])
  let nextCursor = $state<number | undefined>(undefined)
  let search = $state('')
  let loading = $state(false)
  let showModal = $state(false)
  let editing = $state<AnimalWithBreed | null>(null)

  let name = $state('')
  let species = $state<'DOG' | 'CAT' | 'OTHER'>('DOG')
  let speciesOther = $state('')
  let breedId = $state<number | null>(null)
  let birthdate = $state('')
  let color = $state('')

  let breedOptions = $state<Breed[]>([])
  let breedQuery = $state('')

  interface FlatAnimal {
    id: number
    name: string
    species: string
    breedName: string
    birthdate: string
    color: string
  }

  const rows: () => FlatAnimal[] = () =>
    items.map((a) => ({
      id: a.id,
      name: a.name,
      species: a.species,
      breedName: a.breed?.name ?? '-',
      birthdate: a.birthdate ?? '',
      color: a.color ?? '',
    }))

  async function loadBreeds(q: string): Promise<void> {
    const { data, error } = await api.GET('/breeds', {
      params: { query: { limit: 50, ...(q ? { search: q } : {}) } },
    })
    if (error) {
      showApiError(error)
      return
    }
    breedOptions = data?.items ?? []
  }

  async function load(cursor?: number): Promise<void> {
    loading = true
    const { data, error } = await api.GET('/animals', {
      params: {
        query: {
          limit: 50,
          ...(cursor !== undefined ? { cursor } : {}),
          ...(search ? { search } : {}),
        },
      },
    })
    if (error) {
      showApiError(error)
      loading = false
      return
    }
    items = cursor !== undefined ? [...items, ...(data?.items ?? [])] : (data?.items ?? [])
    nextCursor = data?.nextCursor
    loading = false
  }

  function openCreate(): void {
    editing = null
    name = ''
    species = 'DOG'
    speciesOther = ''
    breedId = null
    birthdate = ''
    color = ''
    void loadBreeds('')
    showModal = true
  }

  function openEdit(row: FlatAnimal): void {
    const found = items.find((a) => a.id === row.id)
    if (!found) return
    editing = found
    name = found.name
    species = found.species
    speciesOther = found.speciesOther ?? ''
    breedId = found.breed?.id ?? null
    birthdate = found.birthdate ?? ''
    color = found.color ?? ''
    void loadBreeds('')
    showModal = true
  }

  async function handleSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault()
    const body = {
      name,
      species,
      ...(species === 'OTHER' && speciesOther ? { speciesOther } : {}),
      ...(breedId !== null ? { breedId } : {}),
      ...(birthdate ? { birthdate } : {}),
      ...(color ? { color } : {}),
    }
    if (editing) {
      const { data, error } = await api.PUT('/animals/{id}', {
        params: { path: { id: editing.id } },
        body,
      })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = items.map((a) => (a.id === editing?.id ? data : a))
    } else {
      const { data, error } = await api.POST('/animals', { body })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = [data, ...items]
    }
    showModal = false
  }

  async function handleDelete(id: number): Promise<void> {
    if (!confirm('Delete this animal?')) return
    const { error } = await api.DELETE('/animals/{id}', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    items = items.filter((a) => a.id !== id)
  }

  let openIncidentsId = $state<number | null>(null)
  let incidents = $state<IncidentItem[]>([])
  let incidentsLoading = $state(false)
  let occupancyOpts = $state<{ id: number; label: string }[]>([])

  let incCategory = $state<IncidentCategory>('OTHER')
  let incOccurredOn = $state('')
  let incOccupancyId = $state<number | null>(null)
  let incDescription = $state('')

  function resetIncidentForm(): void {
    incCategory = 'OTHER'
    incOccurredOn = ''
    incOccupancyId = null
    incDescription = ''
  }

  async function loadIncidents(animalId: number): Promise<void> {
    incidentsLoading = true
    const { data, error } = await api.GET('/animals/{animalId}/incidents', {
      params: { path: { animalId } },
    })
    if (error) {
      showApiError(error)
      incidentsLoading = false
      return
    }
    incidents = data?.items ?? []
    // Keep the row's count badge in sync after logging/deleting on this screen
    // (the badge is otherwise only set by the /animals list fetch).
    items = items.map((a) => (a.id === animalId ? { ...a, incidentCount: incidents.length } : a))
    incidentsLoading = false
  }

  async function loadIncidentOccupancies(animalId: number): Promise<void> {
    const { data, error } = await api.GET('/occupancies', {
      params: { query: { animalId, limit: 50 } },
    })
    if (error) {
      showApiError(error)
      return
    }
    occupancyOpts = (data?.items ?? []).map((o) => ({ id: o.id, label: o.unit.streetAddress }))
  }

  async function toggleIncidents(animalId: number): Promise<void> {
    if (openIncidentsId === animalId) {
      openIncidentsId = null
      return
    }
    openIncidentsId = animalId
    resetIncidentForm()
    occupancyOpts = []
    await loadIncidents(animalId)
    if ($currentRole === 'MANAGER' || $currentRole === 'ADMIN') {
      void loadIncidentOccupancies(animalId)
    }
  }

  async function submitIncident(e: SubmitEvent, animalId: number): Promise<void> {
    e.preventDefault()
    const { error } = await api.POST('/animals/{animalId}/incidents', {
      params: { path: { animalId } },
      body: {
        category: incCategory,
        description: incDescription,
        ...(incOccurredOn ? { occurredOn: incOccurredOn } : {}),
        ...(incOccupancyId !== null ? { occupancyId: incOccupancyId } : {}),
      },
    })
    if (error) {
      showApiError(error)
      return
    }
    resetIncidentForm()
    await loadIncidents(animalId)
  }

  async function handleDeleteIncident(id: number, animalId: number): Promise<void> {
    if (!confirm('Delete this incident?')) return
    const { error } = await api.DELETE('/incidents/{id}', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    await loadIncidents(animalId)
  }

  const searchDebounced = debounce(() => void load(), 300)
  $effect(() => {
    void search
    searchDebounced.call()
    return () => searchDebounced.cancel()
  })

  const breedDebounced = debounce((q: string) => void loadBreeds(q), 300)
  $effect(() => {
    if (!showModal) return
    breedDebounced.call(breedQuery)
    return () => breedDebounced.cancel()
  })

  onMount(() => {
    void load()
  })
</script>

<div class="section-header">
  <h1>Animals</h1>
  <div class="section-actions">
    <SearchInput bind:value={search} placeholder="Search by name" />
    <button type="button" class="primary" onclick={openCreate}>+ Add animal</button>
  </div>
</div>

<Table
  items={rows()}
  {loading}
  columns={[
    { key: 'name', label: 'Name' },
    { key: 'species', label: 'Type' },
    { key: 'breedName', label: 'Breed' },
    { key: 'birthdate', label: 'Birthdate' },
    { key: 'color', label: 'Color' },
  ]}
  onEdit={openEdit}
  onDelete={handleDelete}
/>

{#if nextCursor !== undefined}
  <button type="button" class="load-more" onclick={() => void load(nextCursor)} disabled={loading}>
    {loading ? 'Loading…' : 'Load more'}
  </button>
{/if}

<h2>Incidents</h2>
<div class="incidents-list">
  {#each items as a (a.id)}
    <div class="incident-anim">
      <button type="button" class="incident-toggle" onclick={() => void toggleIncidents(a.id)}>
        <span>{a.name}</span>
        <span class="count" class:muted={a.incidentCount === 0}>{a.incidentCount}</span>
      </button>
      {#if openIncidentsId === a.id}
        <div class="incident-panel">
          {#if incidentsLoading}
            <p class="muted">Loading…</p>
          {:else if incidents.length === 0}
            <p class="muted">No incidents logged.</p>
          {:else}
            {#each incidents as i (i.id)}
              <div class="incident-row">
                <strong>{INCIDENT_LABELS[i.category] ?? i.category}</strong>
                <span class="muted"
                  >{i.occurredOn ?? 'date unknown'}{i.unitLabel ? ` - ${i.unitLabel}` : ''}</span
                >
                <div>{i.description}</div>
                <div class="muted">
                  Logged by {i.loggedByEmail} on {new Date(i.createdAt).toLocaleDateString()}
                </div>
                {#if $currentRole === 'ADMIN'}
                  <button
                    type="button"
                    class="link danger"
                    onclick={() => void handleDeleteIncident(i.id, a.id)}
                  >
                    Delete
                  </button>
                {/if}
              </div>
            {/each}
          {/if}
          {#if $currentRole === 'MANAGER' || $currentRole === 'ADMIN'}
            <form class="incident-form" onsubmit={(e) => void submitIncident(e, a.id)}>
              <div class="form-field">
                <label for={`inc-category-${a.id}`}>Category</label>
                <select id={`inc-category-${a.id}`} bind:value={incCategory}>
                  {#each CATEGORY_OPTIONS as cat (cat)}
                    <option value={cat}>{INCIDENT_LABELS[cat]}</option>
                  {/each}
                </select>
              </div>
              <FormField
                label="Date (optional)"
                name={`inc-date-${a.id}`}
                type="date"
                bind:value={incOccurredOn}
              />
              {#if occupancyOpts.length > 0}
                <div class="form-field">
                  <label for={`inc-unit-${a.id}`}>Unit (optional)</label>
                  <select id={`inc-unit-${a.id}`} bind:value={incOccupancyId}>
                    <option value={null}>- No unit -</option>
                    {#each occupancyOpts as opt (opt.id)}
                      <option value={opt.id}>{opt.label}</option>
                    {/each}
                  </select>
                </div>
              {/if}
              <div class="form-field">
                <label for={`inc-desc-${a.id}`}>Description</label>
                <textarea
                  id={`inc-desc-${a.id}`}
                  bind:value={incDescription}
                  maxlength="2000"
                  rows="2"
                  required
                ></textarea>
              </div>
              <button type="submit" class="primary">Log incident</button>
            </form>
          {/if}
        </div>
      {/if}
    </div>
  {/each}
</div>

{#if showModal}
  <Modal title={editing ? 'Edit animal' : 'Add animal'} onclose={() => (showModal = false)}>
    <form onsubmit={handleSubmit}>
      <FormField label="Name" name="name" bind:value={name} required />
      <div class="form-field">
        <label for="species">Type</label>
        <select id="species" bind:value={species}>
          <option value="DOG">Dog</option>
          <option value="CAT">Cat</option>
          <option value="OTHER">Other</option>
        </select>
      </div>
      {#if species === 'OTHER'}
        <FormField label="What kind of animal?" name="speciesOther" bind:value={speciesOther} />
      {/if}
      <div class="form-field">
        <label for="breed">Breed</label>
        <SearchInput bind:value={breedQuery} placeholder="Type to search breeds" />
        <select id="breed" bind:value={breedId}>
          <option value={null}>- No breed -</option>
          {#each breedOptions as breed (breed.id)}
            <option value={breed.id}>{breed.name}</option>
          {/each}
        </select>
      </div>
      <FormField label="Birthdate" name="birthdate" type="date" bind:value={birthdate} />
      <FormField label="Color" name="color" bind:value={color} />
      <button type="submit" class="primary">Save</button>
    </form>
  </Modal>
{/if}

<style>
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  h1 {
    margin: 0;
    font-size: 1.75rem;
  }
  .section-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  button.primary {
    background: var(--brand);
    color: white;
    padding: 6px 14px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }
  button.primary:hover {
    background: var(--brand-hover);
  }
  button.load-more {
    padding: 8px 16px;
    background: var(--surface-2);
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    cursor: pointer;
  }
  button.load-more:hover:not(:disabled) {
    background: var(--surface-3);
  }
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
  }
  .form-field label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  .form-field select {
    padding: 6px 10px;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 0.875rem;
  }
  h2 {
    margin: 24px 0 8px;
    font-size: 1.25rem;
  }
  .incidents-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .incident-anim {
    background: var(--surface-2);
    border: 1px solid var(--line);
    border-radius: 6px;
  }
  .incident-toggle {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: none;
    border: none;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 0.9rem;
    color: var(--ink);
  }
  .incident-toggle .count {
    font-weight: 600;
    color: var(--ink-strong);
  }
  .incident-toggle .count.muted {
    font-weight: 400;
    color: var(--muted);
  }
  .incident-panel {
    padding: 8px 12px 12px;
    border-top: 1px solid var(--line);
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .incident-row {
    padding: 8px;
    background: var(--surface);
    border-radius: 4px;
    font-size: 0.85rem;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .muted {
    color: var(--muted);
    font-size: 0.8rem;
  }
  .link {
    background: none;
    border: none;
    color: var(--brand);
    cursor: pointer;
    padding: 2px 0;
    font-size: 0.85rem;
    text-decoration: underline;
    align-self: flex-start;
  }
  .link.danger {
    color: var(--err-ink);
  }
  .incident-form {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 8px;
    border-top: 1px solid var(--line);
  }
  .incident-form textarea {
    padding: 6px 10px;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 0.875rem;
    font-family: inherit;
    resize: vertical;
  }
</style>
